import _ from "lodash";
import { useEffect, useState } from "react";
import { Button, Dropdown, DropdownItemProps, Form, FormProps, Input, Modal, Segment, Table } from "semantic-ui-react";
import { dialogControl } from "../../controls/dialog-control";
import { useCurrentUser } from "../../hooks/auth-hook";
import { ModalControlData, useModalControl } from "../../hooks/commonHooks";
import { useFormConnect, useFormData } from "../../hooks/formHooks";
import { restRequest } from "../../http";
import { PageData } from "../../models/common_model";
import { DropdownOption } from "../../models/testing_model";
import { UpdateLimitData } from "../../models/update_limit_model";
import { fieldError, formatDateTime } from "../../utils";
import { Page } from "../commons/Page";
import { UpdateLimitMeasurementComponent } from "./UpdateLimitMeasurementComponent";

export function UpdateLimitComponent(props: {}) {
    const currentUser = useCurrentUser();

    const [searchData, setSearchField] = useFormData<any>({});
    const [page, setSearchData] = useFormConnect<any, PageData<UpdateLimitData>>({}, 'update_limit/search');

    const editModalControl = useModalControl();
    const limitControl = useModalControl();
    const [currentId, setCurrentId] = useState<number>(0);
    const [reloadNumber, setReloadNumber] = useState(0);

    return <>
        <Segment.Group>
            <Segment attached="top">
                <Form onSubmit={e => setSearchData(searchData)}>
                    <Form.Group>
                        <Form.Field inline>
                            <Input placeholder="Product" name="product" value={searchData['product'] || ''} onChange={setSearchField} />
                        </Form.Field>
                        <Form.Field inline>
                            <Input placeholder="Testing Station" name="testing_station" value={searchData['testing_station'] || ''} onChange={setSearchField} />
                        </Form.Field>

                        <div><Button icon="search" type="submit"></Button></div>
                    </Form.Group>
                </Form>
            </Segment>
            <Segment attached="bottom">
                <Table striped>
                    <Table.Header>
                        <Table.Row>
                            <th>Product</th>
                            <th>Testing Station</th>
                            <th>Created By</th>
                            <th>Created At</th>
                            <th>{currentUser.permissions.update_limit.create && <Button color="blue" icon="add" content="Product & Testing Station" size="mini" onClick={e => {
                                setCurrentId(0);
                                editModalControl.setOpen(true);
                            }} />}</th>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {page?.items.map(obj => {
                            return <tr key={obj.id}>
                                <td>{obj.product}</td>
                                <td>{obj.testing_station}</td>
                                <td>{obj.created_by_name}</td>
                                <td>{formatDateTime(obj.created_at)}</td>
                                <td>
                                    {currentUser.permissions.update_limit.update && <Button onClick={e => {
                                        setCurrentId(obj.id);
                                        editModalControl.setOpen(true);
                                    }} color="blue" icon="edit" size="mini" />}

                                    {currentUser.permissions.update_limit.measurement.view && <Button size="mini" onClick={e => {
                                        setCurrentId(obj.id);
                                        limitControl.setOpen(true);
                                    }}>Measurements</Button>}
                                </td>
                            </tr>
                        })}
                    </Table.Body>
                </Table>
                <Page data={page} updatePage={setSearchData}></Page>
            </Segment>
        </Segment.Group>

        <Editor id={currentId} modelControl={editModalControl} onFinish={result => {
            setSearchData((searchData: any) => ({ ...searchData, refresh: (searchData.refresh || 1) + 1 }));
            editModalControl.setOpen(false)
            setReloadNumber(n => n + 1);
        }}></Editor>

        <Modal {...limitControl.props} size="fullscreen">
            <Modal.Header>Update Limit Measurements</Modal.Header>
            <Modal.Content>
                <UpdateLimitMeasurementComponent updateId={currentId}></UpdateLimitMeasurementComponent>
            </Modal.Content>
        </Modal>
    </>
}

function Editor(props: { id: number, allRoles?: { id: number, name: string }[], modelControl: ModalControlData, onFinish: (data: UpdateLimitData) => void }) {
    const { id, modelControl, onFinish } = props;
    const isCreate = !id;

    const [data, setField, setData] = useFormData<UpdateLimitData>({} as any);
    const [invalidError, setInvalidError] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [productOptions, setProductOptions] = useState<DropdownItemProps[]>([]);
    const [stationOptions, setStationOptions] = useState<DropdownItemProps[]>([]);
    const [testingResultDropdownOptions, setTestingResultDropdownOptions] = useState<DropdownOption[]>([]);

    useEffect(() => {
        restRequest<DropdownOption[]>('svn_repository/result_dropdown_options').then(setTestingResultDropdownOptions)
    }, []);

    useEffect(() => {
        const options = [{ key: '', text: 'None', value: '' }, ...testingResultDropdownOptions.map(t => ({ key: t.name, text: t.name, value: t.name }))];
        setProductOptions(options);
        if (!options.find(o => o.value == data.product)) {
            setField(null, { name: 'product', value: '' });
            setField(null, { name: 'testing_station', value: '' });
        }
    }, [testingResultDropdownOptions]);

    useEffect(() => {
        const product = testingResultDropdownOptions.find(t => t.name == data.product);
        let options: DropdownItemProps[];
        if (product) {
            let stations: string[] = [];
            for(const c of product.children) {
                stations = stations.concat(c.children.map(cc => cc.name))
            }
            options = [{ key: '', text: 'None', value: '' }, ..._.uniq(stations).map(s => ({ key: s, text: s, value: s }))];
        } else {
            options = [{ key: '', text: 'None', value: '' }];
        }

        setStationOptions(options);

        if (!options.find(o => o.value == data.testing_station)) {
            setField(null, { name: 'station', value: '' });
        }
    }, [testingResultDropdownOptions, data.product]);


    useEffect(() => {
        setData({ admin: false } as any);
        setInvalidError({});
        if (id) {
            setLoading(true);
            restRequest<UpdateLimitData>('update_limit/' + id).then(setData).finally(() => setLoading(false))
        }
    }, [id])

    const onSubmit = (event: React.FormEvent<HTMLFormElement>, formProps: FormProps) => {
        event.preventDefault();

        setLoading(true);

        restRequest<UpdateLimitData>(`update_limit/${isCreate ? '' : id}`, data, isCreate ? 'post' : 'put').then(result => {
            props.onFinish(result);
            setInvalidError({});
        }, resp => {
            if (resp.status === 2) {
                setInvalidError(resp.data);
            }
        }).finally(() => setLoading(false));
    }


    return <>
        <Modal {...modelControl.props} size="large">
            <Modal.Header>{isCreate ? 'Create' : 'Update'} Product & Testing Station </Modal.Header>
            <Modal.Content>
                <Form onSubmit={onSubmit} loading={loading}>
                    <Form.Group widths="equal">
                        <Form.Input label="Product" style={{ minWidth: '10rem' }} required error={fieldError('product', invalidError)}>
                            <Dropdown name="product" value={data.product || ''} onChange={setField} options={productOptions} search selection fluid></Dropdown>
                        </Form.Input>

                        <Form.Input label="Testing Station" style={{ minWidth: '10rem' }} required error={fieldError('testing_station', invalidError)}>
                            <Dropdown name="testing_station" value={data.testing_station || ''} onChange={setField} options={stationOptions} search selection fluid></Dropdown>
                        </Form.Input>
                    </Form.Group>
                    <div>
                        <Button primary>Submit</Button>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        <Button as="a" onClick={e => props.modelControl.setOpen(false)}>Cancel</Button>
                    </div>
                </Form>
            </Modal.Content>
        </Modal>
    </>
}